import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  IconButton,
  Divider,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CloseIcon from "@mui/icons-material/Close";
import { styled } from "@mui/material/styles";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { useDispatch } from "react-redux";
import Notification from "../../components/common/Notification";
import { useAppSelector } from "../../redux/hooks";
import Loader from "../../components/common/Loader";
import JoditEditor from "jodit-react";
import {
  createDigitalMedia,
  getAllDigitalMedia,
  resetDigitalMediaState,
  updateDigitalMedia,
} from "../../redux/slices/digitalMediaSlice";

const StyledFormContainer = styled(Box)({
  width: "100%",
  height: "100%",
  backgroundColor: "background.default",
});

const StyledFormSection = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3),
  backgroundColor: theme.palette.background.paper,
  borderRadius: theme.shape.borderRadius,
  boxShadow: theme.shadows[1],
  marginBottom: theme.spacing(3),
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(2),
  },
}));

const StyledTypography = styled(Typography)({
  fontFamily: "Poppins, sans-serif !important",
});

const Tag = styled(Box)(({ theme }) => ({
  display: "inline-flex",
  alignItems: "center",
  margin: "4px",
  padding: "4px 12px",
  backgroundColor: theme.palette.primary.light,
  borderRadius: "16px",
  "& .MuiSvgIcon-root": {
    fontSize: "18px",
    marginLeft: "8px",
    cursor: "pointer",
    "&:hover": {
      color: theme.palette.error.main,
    },
  },
}));

const TagsContainer = styled(Box)({
  display: "flex",
  flexWrap: "wrap",
  gap: "8px",
  padding: "8px 0",
});

const Form = ({ editData: propEditData }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { id } = useParams();
  const location = useLocation();
  const initialData = location.state?.initialData;
  const editor = useRef(null);

  const { loading, error, success } = useAppSelector(
    (state) => state.electricMedia
  );

  const [notification, setNotification] = useState(null);
  const [editData, setEditData] = useState(propEditData || initialData || null);
  const [formData, setFormData] = useState({
    name: "",
    slug: "",
    description: "",
    channelName: "",
    channelLink: "",
    youtubeUrl: "",
    date: "",
    status: "active",
    seoTitle: "",
    seoDescription: "",
    seoKeywords: "",
  });

  const generateSlug = (text) => {
    return text
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, "")
      .replace(/\s+/g, "-")
      .replace(/-+/g, "-")
      .trim();
  };

  useEffect(() => {
    if (id && !editData) {
      dispatch(getAllDigitalMedia())
        .unwrap()
        .then((response) => {
          const media = response.data.find((media) => media._id === id);
          if (media) {
            setEditData(media);
          }
        })
        .catch((error) => {
          setNotification({
            type: "error",
            message: "Failed to fetch electric media details",
          });
        });
    }
  }, [id, editData, dispatch]);

  useEffect(() => {
    if (success) {
      setNotification({
        type: "success",
        message: editData
          ? "Digital Media updated successfully!"
          : "Digital Media created successfully!",
      });
      setTimeout(() => {
        setNotification(null);
        dispatch(resetDigitalMediaState());
        // Use replace to ensure clean navigation
        navigate("/digital-media", { replace: true });
      }, 1500);
    }
  }, [success, editData, dispatch, navigate]);

  useEffect(() => {
    if (error) {
      setNotification({
        type: "error",
        message:
          typeof error === "string"
            ? error
            : error?.message || "Something went wrong!",
      });
    }
  }, [error]);

  useEffect(() => {
    if (editData) {
      setFormData({
        name: editData.name || "",
        slug: editData.slug || "",
        description: editData.description || "",
        channelName: editData.channelName || "",
        channelLink: editData.channelLink || "",
        youtubeUrl: editData.youtubeUrl || "",
        date: editData.date
          ? new Date(editData.date).toISOString().split("T")[0]
          : "",
        status: editData.status || "active",
        seoTitle: editData.seoTitle || "",
        seoDescription: editData.seoDescription || "",
        seoKeywords: editData.seoKeywords || "",
      });
    }
  }, [editData]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === "name") {
      const slug = generateSlug(value);
      setFormData((prev) => ({
        ...prev,
        [name]: value,
        slug: slug,
      }));
    } else if (name === "seoKeywords") {
      const cleanedKeywords = value
        .split(",")
        .map((keyword) => keyword.trim())
        .filter((keyword) => keyword)
        .join(",");

      setFormData((prev) => ({
        ...prev,
        [name]: cleanedKeywords,
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const handleKeywordChange = (e) => {
    const value = e.target.value;
    const keywords = value.split(",");

    if (keywords.length > 15) {
      setNotification({
        type: "error",
        message: "Maximum 15 keywords allowed",
      });
      return;
    }

    setFormData((prev) => ({
      ...prev,
      seoKeywords: value,
    }));
  };

  const removeKeyword = (indexToRemove) => {
    const keywords = formData.seoKeywords.split(",");
    const newKeywords = keywords.filter((_, index) => index !== indexToRemove);
    setFormData((prev) => ({
      ...prev,
      seoKeywords: newKeywords.join(","),
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    setNotification(null);

    try {
      if (!formData.name.trim()) {
        setNotification({
          type: "error",
          message: "Organ Donor Name is required",
        });
        return;
      }

      if (!formData.youtubeUrl.trim()) {
        setNotification({
          type: "error",
          message: "YouTube URL is required",
        });
        return;
      }

      if (!formData.channelName.trim()) {
        setNotification({
          type: "error",
          message: "Channel name is required",
        });
        return;
      }

      if (editData) {
        const result = await dispatch(
          updateDigitalMedia({
            id: editData._id,
            data: formData,
          })
        ).unwrap();

        if (result) {
          setNotification({
            type: "success",
            message: "Electronic Media updated successfully!",
          });

          setTimeout(() => {
            navigate("/electronic-media", { replace: true });
          }, 1500);
        }
      } else {
        const result = await dispatch(createDigitalMedia(formData)).unwrap();

        if (result) {
          setNotification({
            type: "success",
            message: "Digital Media created successfully!",
          });

          setTimeout(() => {
            navigate("/digital-media", { replace: true });
          }, 1500);
        }
      }
    } catch (err) {
      console.error("Form submission error:", err);
      setNotification({
        type: "error",
        message: err || "Operation failed. Please try again.",
      });
    }
  };

  const handleCancel = () => {
    navigate("/digital-media");
  };

  return (
    <StyledFormContainer className="poppins-font">
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          px: 3,
          py: 2,
          borderBottom: "1px solid",
          borderColor: "divider",
          backgroundColor: "background.paper",
          position: "sticky",
          top: 0,
          zIndex: 1100,
        }}
        className="poppins-font"
      >
        <IconButton
          onClick={handleCancel}
          sx={{
            mr: 2,
            bgcolor: "background.paper",
            "&:hover": { bgcolor: "action.hover" },
          }}
        >
          <ArrowBackIcon />
        </IconButton>
        <StyledTypography variant="h5" component="h1" fontWeight="bold">
          {editData ? "Edit" : "Add New"} Electronic Media
        </StyledTypography>
      </Box>

      {/* Main Form Content */}
      <Box sx={{ p: 3 }}>
        <form onSubmit={handleSubmit} className="poppins-font">
          <StyledFormSection>
            <Typography
              variant="h6"
              className="poppins-font"
              gutterBottom
              sx={{ mb: 3, fontWeight: 600 }}
            >
              Basic Information
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
              <TextField
                fullWidth
                label="Organ Donor Name *"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                placeholder="Enter organ donor name"
                className="poppins-font"
              />

              <TextField
                fullWidth
                label="Slug Title"
                name="slug"
                value={formData.slug}
                disabled
                helperText="Auto-generated from organ donor name"
                className="poppins-font"
                sx={{
                  "& .MuiInputBase-input.Mui-disabled": {
                    WebkitTextFillColor: "#666",
                  },
                }}
              />

              <Box>
                <Typography
                  variant="subtitle1"
                  gutterBottom
                  sx={{ fontWeight: 600 }}
                >
                  Description
                </Typography>
                <Box
                  sx={{
                    width: "100%",
                    "& .jodit-container": {
                      width: "100% !important",
                      maxWidth: "100%",
                    },
                    "& .jodit-wysiwyg": {
                      padding: "16px",
                    },
                  }}
                >
                  <JoditEditor
                    ref={editor}
                    value={formData.description}
                    onBlur={(newContent) => {
                      setFormData((prev) => ({
                        ...prev,
                        description: newContent,
                      }));
                    }}
                    config={{
                      readonly: false,
                      placeholder: "Enter organ donor description...",
                      height: 200,
                      buttons: [
                        "bold",
                        "italic",
                        "underline",
                        "|",
                        "ul",
                        "ol",
                        "|",
                        "link",
                      ],
                    }}
                  />
                </Box>
              </Box>

              <TextField
                fullWidth
                label="Date *"
                name="date"
                type="date"
                value={formData.date}
                onChange={handleChange}
                required
                InputLabelProps={{
                  shrink: true,
                }}
                inputProps={{
                  max: new Date().toISOString().split("T")[0],
                }}
                className="poppins-font"
              />

              <FormControl fullWidth required>
                <InputLabel className="poppins-font">Status *</InputLabel>
                <Select
                  name="status"
                  value={formData.status}
                  label="Status *"
                  onChange={handleChange}
                  className="poppins-font"
                >
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="inactive">Inactive</MenuItem>
                </Select>
              </FormControl>
            </Box>
          </StyledFormSection>

          {/* Channel Information Section */}
          <StyledFormSection>
            <Typography
              variant="h6"
              className="poppins-font"
              gutterBottom
              sx={{ mb: 3, fontWeight: 600 }}
            >
              Channel Information
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
              <TextField
                fullWidth
                label="Channel Name *"
                name="channelName"
                value={formData.channelName}
                onChange={handleChange}
                required
                placeholder="e.g., News Channel, BBC News, etc."
                className="poppins-font"
              />

              <TextField
                fullWidth
                label="Channel Link"
                name="channelLink"
                value={formData.channelLink}
                onChange={handleChange}
                placeholder="https://www.youtube.com/channel/..."
                className="poppins-font"
              />

              <TextField
                fullWidth
                label="YouTube Video URL *"
                name="youtubeUrl"
                value={formData.youtubeUrl}
                onChange={handleChange}
                required
                placeholder="https://www.youtube.com/watch?v=..."
                className="poppins-font"
              />
            </Box>
          </StyledFormSection>

          {/* SEO Settings Section */}
          <StyledFormSection>
            <Typography
              className="poppins-font"
              variant="h6"
              gutterBottom
              sx={{ fontWeight: 600 }}
            >
              SEO Settings
            </Typography>
            <Divider sx={{ mb: 3 }} />

            <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
              <TextField
                fullWidth
                label="SEO Title"
                name="seoTitle"
                value={formData.seoTitle}
                onChange={handleChange}
                placeholder="SEO optimized title for search engines"
                className="poppins-font"
              />

              <TextField
                fullWidth
                label="SEO Description"
                name="seoDescription"
                value={formData.seoDescription}
                onChange={handleChange}
                multiline
                rows={3}
                placeholder="Brief description for search engines (150-160 characters recommended)"
                className="poppins-font"
              />

              <TextField
                fullWidth
                label="SEO Keywords"
                name="seoKeywords"
                value={formData.seoKeywords}
                onChange={handleKeywordChange}
                placeholder="Enter keywords separated by commas (max 15)"
                helperText="Type keywords separated by commas. Example: organ, donor, electric, media, etc."
                className="poppins-font"
              />

              {/* Keywords Display */}
              <TagsContainer>
                {formData.seoKeywords.split(",").map((keyword, index) =>
                  keyword.trim() ? (
                    <Tag key={index}>
                      <Typography variant="body2" component="span">
                        {keyword.trim()}
                      </Typography>
                      <IconButton
                        size="small"
                        onClick={() => removeKeyword(index)}
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </Tag>
                  ) : null
                )}
              </TagsContainer>
            </Box>
          </StyledFormSection>

          {/* Form Actions */}
          <Box
            sx={{
              position: "sticky",
              bottom: 0,
              left: 0,
              right: 0,
              py: 2,
              mt: 3,
              borderTop: "1px solid",
              borderColor: "divider",
              backgroundColor: "background.paper",
              display: "flex",
              justifyContent: "flex-end",
              gap: 2,
              zIndex: 1000,
            }}
          >
            <Button
              onClick={handleCancel}
              variant="outlined"
              sx={{ minWidth: 120 }}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={loading}
              sx={{ minWidth: 120 }}
              className="poppins-font"
            >
              {loading
                ? editData
                  ? "Updating..."
                  : "Creating..."
                : editData
                ? "Update"
                : "Create"}
            </Button>
          </Box>
        </form>
      </Box>

      {/* Notification */}
      {notification && (
        <Notification
          type={notification.type}
          message={notification.message}
          onClose={() => setNotification(null)}
          autoClose={notification.type === "success"}
          duration={3000}
        />
      )}

      {/* Loader */}
      {loading && <Loader />}
    </StyledFormContainer>
  );
};

export default Form;
